// Server emoji and stickers: pictures a server's owners and admins upload (or import from Discord), kept in the
// server's room (metadata) and R2 (servers/<serverId>/emoji/<id>, servers/<serverId>/stickers/<id>).
//
// Messages store custom emoji as Discord does: "<:name:id>", or "<a:name:id>" when animated. The id decides the
// picture, so a token keeps working after a rename; the name is only the fallback text (":name:") once the emoji is
// deleted, or for a token pasted from somewhere else. People type ":name:", and both the composer (for the sender's
// own optimistic copy) and the room encode it with encodeCustomEmoji, as with @mentions.
// A reaction with a custom emoji uses the same token as its key.

import { cleanName } from "./cleanName.ts";

export interface CustomEmoji {
  /** ULID; never changes, so the picture can be cached forever. */
  id: string;
  /** 2–32 letters, digits and underscores, unique in the server (ignoring case). */
  name: string;
  animated: boolean;
  createdBy: string;
  createdAt: number;
}

export type StickerFormat = "png" | "apng" | "gif" | "webp";

export interface Sticker {
  id: string;
  /** 2–30 characters, unique in the server (ignoring case). */
  name: string;
  format: StickerFormat;
  createdBy: string;
  createdAt: number;
}

export const MAX_EMOJI_BYTES = 256 * 1024;
export const MAX_STICKER_BYTES = 512 * 1024;
/** Per server: far past Discord's (250 emoji, 60 stickers); file sizes stay capped above. */
export const MAX_SERVER_EMOJI = 1000;
export const MAX_SERVER_STICKERS = 1000;

export const EMOJI_NAME = /^[A-Za-z0-9_]{2,32}$/;
/** A stored custom emoji. Groups: "a" when animated, name, id. */
export const CUSTOM_EMOJI_TOKEN = /<(a?):([A-Za-z0-9_]{2,32}):([0-9A-Za-z]{1,32})>/g;
const ONE_TOKEN = /^<(a?):([A-Za-z0-9_]{2,32}):([0-9A-Za-z]{1,32})>$/;

/** Where the picture is served (to members of the server only). */
export const emojiUrl = (serverId: string, id: string) => `/api/servers/${serverId}/emoji/${id}`;
export const stickerUrl = (serverId: string, id: string) => `/api/servers/${serverId}/stickers/${id}`;

export const emojiToken = (e: Pick<CustomEmoji, "id" | "name" | "animated">) => `<${e.animated ? "a" : ""}:${e.name}:${e.id}>`;

/** A token's parts, or null when the string isn't exactly one custom emoji token. */
export function parseEmojiToken(s: string): { name: string; id: string; animated: boolean } | null {
  const m = ONE_TOKEN.exec(s);
  return m ? { animated: m[1] === "a", name: m[2]!, id: m[3]! } : null;
}

/** Emoji names: what's typed between colons, cleaned to Discord's rules (letters, digits, "_"). */
export function cleanEmojiName(raw: string): string {
  return raw
    .trim()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\.[a-z0-9]{2,4}$/i, "")
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
}

export function cleanStickerName(raw: string): string {
  return cleanName(cleanName(raw).replace(/\.[a-z0-9]{2,4}$/i, ""), 30);
}

// Existing tokens (<…>), links and code are skipped; ":name:" outside them becomes a token when the server has it.
const ENCODE = /(\\.)|(`+)[\s\S]*?\2|(<[^\s<>]+>)|((?:https?:\/\/|www\.)\S+)|:([A-Za-z0-9_]{2,32}):/g;

/** Turns typed ":name:" into "<:name:id>" for this server's emoji (exact name first, then ignoring case). */
export function encodeCustomEmoji(text: string, emojis: Pick<CustomEmoji, "id" | "name" | "animated">[]): string {
  if (!emojis.length || !text.includes(":")) return text;
  const exact = new Map(emojis.map((e) => [e.name, e]));
  const lower = new Map(emojis.map((e) => [e.name.toLowerCase(), e]));
  return text.replace(ENCODE, (whole, _esc, _code, _tok, _url, name: string | undefined) => {
    if (!name) return whole;
    const e = exact.get(name) ?? lower.get(name.toLowerCase());
    return e ? emojiToken(e) : whole;
  });
}

/** Turns tokens back into ":name:" (for editing, copying and anything shown as plain text). */
export function decodeCustomEmoji(text: string, byId?: Map<string, Pick<CustomEmoji, "name">>): string {
  if (!text.includes("<")) return text;
  return text.replace(CUSTOM_EMOJI_TOKEN, (_, _a, name: string, id: string) => `:${byId?.get(id)?.name ?? name}:`);
}

/** The custom emoji id in a reaction key or token, or null for a Unicode emoji. */
export const customEmojiId = (key: string) => parseEmojiToken(key)?.id ?? null;

/**
 * What an uploaded picture really is, from its first bytes (never from the name or the declared type): PNG (APNG when
 * it has an animation chunk before the image data), GIF (animated when it has more than one frame), or WebP
 * (animated with an ANIM chunk). Anything else, SVG included, is null.
 */
export function sniffImage(bytes: Uint8Array): { type: "image/png" | "image/gif" | "image/webp"; animated: boolean; width?: number; height?: number } | null {
  const ascii = (from: number, n: number) => String.fromCharCode(...bytes.subarray(from, from + n));
  const u32be = (i: number) => ((bytes[i]! << 24) | (bytes[i + 1]! << 16) | (bytes[i + 2]! << 8) | bytes[i + 3]!) >>> 0;
  const u16le = (i: number) => bytes[i]! | (bytes[i + 1]! << 8);
  const u24le = (i: number) => bytes[i]! | (bytes[i + 1]! << 8) | (bytes[i + 2]! << 16);
  if (bytes.length >= 24 && bytes[0] === 0x89 && ascii(1, 3) === "PNG" && ascii(12, 4) === "IHDR") {
    let animated = false;
    for (let i = 8; i + 8 <= bytes.length; ) {
      const len = u32be(i);
      const kind = ascii(i + 4, 4);
      if (kind === "acTL") animated = true;
      if (kind === "IDAT" || kind === "IEND") break;
      i += 12 + len;
    }
    return { type: "image/png", animated, width: u32be(16), height: u32be(20) };
  }
  if (bytes.length >= 10 && (ascii(0, 6) === "GIF87a" || ascii(0, 6) === "GIF89a")) {
    return { type: "image/gif", animated: gifFrames(bytes) > 1, width: u16le(6), height: u16le(8) };
  }
  if (bytes.length >= 30 && ascii(0, 4) === "RIFF" && ascii(8, 4) === "WEBP") {
    const chunk = ascii(12, 4);
    if (chunk === "VP8X") return { type: "image/webp", animated: (bytes[20]! & 0x02) !== 0, width: u24le(24) + 1, height: u24le(27) + 1 };
    if (chunk === "VP8L") {
      const b = bytes.subarray(21, 25);
      return { type: "image/webp", animated: false, width: 1 + (((b[1]! & 0x3f) << 8) | b[0]!), height: 1 + (((b[3]! & 0xf) << 10) | (b[2]! << 2) | ((b[1]! & 0xc0) >> 6)) };
    }
    if (chunk === "VP8 ") return { type: "image/webp", animated: false, width: u16le(26) & 0x3fff, height: u16le(28) & 0x3fff };
    return { type: "image/webp", animated: false };
  }
  return null;
}

/** Counts a GIF's frames (stops at 2: that's all anyone needs to know). */
function gifFrames(b: Uint8Array): number {
  let i = 13;
  if (b[10]! & 0x80) i += 3 * (1 << ((b[10]! & 7) + 1)); // global colour table
  let frames = 0;
  const skipBlocks = () => {
    while (i < b.length && b[i] !== 0) i += b[i]! + 1;
    i++;
  };
  while (i < b.length) {
    const t = b[i]!;
    if (t === 0x3b) break; // trailer
    if (t === 0x21) {
      i += 2; // extension introducer + label
      skipBlocks();
    } else if (t === 0x2c) {
      if (++frames > 1) return frames;
      const packed = b[i + 9]!;
      i += 10;
      if (packed & 0x80) i += 3 * (1 << ((packed & 7) + 1)); // local colour table
      i++; // LZW minimum code size
      skipBlocks();
    } else break;
  }
  return frames;
}

// ---- Import from Discord ----

/** A Discord server the bot is in, as offered to pick from. */
export interface DiscordGuild {
  id: string;
  name: string;
  /** Icon picture URL on Discord's CDN, or null. */
  icon: string | null;
}

/** One line of the import's progress stream (newline-delimited JSON). */
export type DiscordImportEvent =
  | { t: "start"; emojis: number; stickers: number }
  | { t: "item"; kind: "emoji" | "sticker"; name: string; status: "imported" | "skipped" | "failed"; reason?: string; done: number; total: number }
  | { t: "done"; imported: number; skipped: { kind: "emoji" | "sticker"; name: string; reason: string }[]; failed: { kind: "emoji" | "sticker"; name: string; reason: string }[] }
  | { t: "error"; message: string };
